/**
 * Reading the stored session token without letting a bad one take the app down.
 *
 * Both places that decode the token called `jwtDecode` directly on whatever was
 * in localStorage. `jwt-decode` throws on anything malformed, neither call site
 * caught it, and there is no error boundary anywhere in the tree -- so a single
 * corrupted value unmounted the entire application. The user got a white page:
 * no login redirect, no error, no way back except clearing site data by hand.
 *
 * That is reachable without anything exotic: a truncated write, a token from an
 * older login shape, a half-finished migration, or a developer pasting a value
 * to test something. The cost of getting it wrong is total and the recovery is
 * not discoverable.
 *
 * A malformed token is treated as NO token, which is both true and useful: the
 * app then does what it already does for a signed-out visitor and sends them to
 * sign in.
 */

import { jwtDecode } from 'jwt-decode';

export const TOKEN_KEY = 'token';

/**
 * Reads and decodes the stored session token.
 *
 * Returns null for absent, unreadable or malformed -- deliberately collapsing
 * those cases, because every caller does the same thing with all three and
 * distinguishing them would invite someone to treat "corrupt" as "signed in".
 */
export const readSessionClaims = <T extends object>(): T | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(TOKEN_KEY);
  } catch {
    // Storage can throw outright: Safari private mode, a disabled-cookies
    // profile, or an embedded webview. Not being able to read is the same
    // outcome as having nothing to read.
    return null;
  }

  if (!raw) return null;

  try {
    return jwtDecode<T>(raw);
  } catch {
    return null;
  }
};

/** Removes the stored token. Safe to call when storage itself is unavailable. */
export const clearSession = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing to clear if storage cannot be reached */
  }
};

/**
 * Whether a usable session exists.
 *
 * PrivateRoute previously gated on `!!localStorage.getItem('token')` -- the
 * presence of a string, not a readable one. A corrupt token therefore passed
 * the guard and then crashed the page it had just admitted the user to.
 */
export const hasReadableSession = (): boolean =>
  readSessionClaims<Record<string, unknown>>() !== null;
